/*
 * The help after the checkout (snippets/cart-return-help.liquid), in the
 * drawer and on the cart page.
 *
 * Thomas 18-10: a customer who comes back from the checkout to the cart gets
 * the shortest way on, in one calm block: what closes the gap to free
 * shipping, and how to ask us. No popup, no countdown, no discount code.
 *
 * When: after this browser went to the checkout (a submit of the cart with
 * the checkout button, remembered in sessionStorage) or came from it
 * (document.referrer on /checkouts/), while the cart holds something. An
 * empty cart, after a purchase too, forgets it.
 *
 * What: below Cortex's free shipping threshold, at most two products whose
 * price closes the gap: the add card's own product first when it is one of
 * them, then the cheapest; never what the suggestion row on the same screen
 * already offers. While the block offers something, the add card ("Hoort bij
 * je trui") steps aside (data-return-help-shown on the drawer or the cart
 * page, assets/cart-items.css), so the customer never sees one hat twice or
 * two hats with one name and two prices (coordinator review 18-10).
 * Otherwise only the line with e-mail and WhatsApp. The threshold is Cortex's
 * (window.JapCortex.ready, state.shipping); without an answer only the
 * contact line shows.
 *
 * Measured with the Cortex pixel: CartCheckoutReturn once per session when
 * the block first shows ({ context, gap in cents or null, offered }); an add
 * from it is CartUpsellAdd with strategy 'checkout_return'
 * (assets/cart-set-upsell.js).
 */
(() => {
  if (window.JapCartReturnHelp) return;
  window.JapCartReturnHelp = true;

  const SELECTOR = '[data-cart-return-help]';
  // Also read by the inline script of snippets/cart-return-help.liquid.
  const FLAG_KEY = 'jc-terug-uit-kassa';
  const REPORTED_KEY = 'jc-terug-uit-kassa-gemeld';
  const MAX_OFFERS = 2;
  // The add card's product: offered first here, while the card itself steps aside.
  const ADD_CARD_PRODUCT = '[data-cart-add-card]:not([hidden]) [data-upsell-add]';
  // Products the suggestion row on the same screen already offers.
  const OFFERED_ELSEWHERE = 'cart-set-upsell [data-variant-id]';
  // On the drawer or the cart page while the block offers something.
  const SHOWN_ATTRIBUTE = 'data-return-help-shown';

  const session = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        if (value === null) sessionStorage.removeItem(key);
        else sessionStorage.setItem(key, value);
      } catch (error) {
        // No storage: the block shows only on the page that came from the checkout.
      }
    }
  };

  if (/\/checkouts?\//.test(document.referrer)) session.set(FLAG_KEY, String(Date.now()));
  const cameBack = () => Boolean(session.get(FLAG_KEY));

  // Going to the checkout: remembered for the way back.
  document.addEventListener(
    'submit',
    (event) => {
      if (event.submitter && event.submitter.name === 'checkout') session.set(FLAG_KEY, String(Date.now()));
    },
    true
  );

  // Cortex's answer; the blocks hold their place unseen until it is known.
  let cortexState = null;
  let cortexKnown = false;
  // As long as the other cart scripts wait for Cortex (assets/jc-cortex.js).
  const ANSWER_TIMEOUT_MS = 8000;

  function track(name, data) {
    const payload = { ...data, source: 'theme' };
    if (window.NanoPixel) {
      window.NanoPixel.track(name, payload);
    } else {
      window.NanoPixelQueue = window.NanoPixelQueue || [];
      window.NanoPixelQueue.push([name, payload]);
    }
  }

  /** Cents still missing to free shipping, or null when shipping is free, or nothing is known. */
  function gapFor(total) {
    const shipping = cortexState && cortexState.shipping;
    if (!shipping || !(shipping.price > 0) || !(shipping.freeFrom > 0) || !Number.isFinite(total)) return null;
    return total < shipping.freeFrom ? shipping.freeFrom - total : null;
  }

  function render(block) {
    if (!cortexKnown) return;
    block.dataset.mode = 'rendered';
    const scope = block.closest('cart-drawer, .cc-main-cart') || document;
    const empty = scope.querySelector('cart-items')?.dataset.empty === 'true';
    // A purchase or an emptied cart: nothing to come back to.
    if (empty) session.set(FLAG_KEY, null);
    if (empty || !cameBack()) {
      block.hidden = true;
      if (scope !== document) scope.removeAttribute(SHOWN_ATTRIBUTE);
      return;
    }

    const gap = gapFor(Number(block.dataset.cartTotal));
    const elsewhere = new Set([...scope.querySelectorAll(OFFERED_ELSEWHERE)].map((element) => element.dataset.variantId));
    const cardVariant = scope.querySelector(ADD_CARD_PRODUCT)?.dataset.variantId || block.dataset.cardVariant || '';
    // Remembered on the block: once the card stepped aside it no longer matches ADD_CARD_PRODUCT.
    block.dataset.cardVariant = cardVariant;
    const rank = (item) => (item.dataset.variantId === cardVariant ? -1 : Number(item.dataset.price));
    const items = [...block.querySelectorAll('[data-return-item]')];
    const offers =
      gap === null
        ? []
        : items
            .filter((item) => Number(item.dataset.price) >= gap && !elsewhere.has(item.dataset.variantId))
            .sort((a, b) => rank(a) - rank(b))
            .slice(0, MAX_OFFERS);
    if (scope !== document) scope.toggleAttribute(SHOWN_ATTRIBUTE, offers.length > 0);
    // In that order, without moving anything in the DOM (the list is a flex column).
    items.forEach((item) => {
      item.hidden = !offers.includes(item);
      item.style.order = item.hidden ? '' : String(offers.indexOf(item));
    });
    block.querySelector('[data-return-bridge]').hidden = offers.length === 0;
    block.querySelector('[data-return-items]').hidden = offers.length === 0;
    const hasAsk = Boolean(block.querySelector('.cart-return-help__ask'));
    block.hidden = offers.length === 0 && !hasAsk;
    block.removeAttribute('data-waiting');
    block.dataset.gap = gap === null ? '' : String(gap);
    block.dataset.offered = String(offers.length);
    if (!block.hidden) seen.observe(block);
  }

  // Reported when the block is really on screen (the drawer is rendered on
  // every page, also while closed).
  const seen = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      seen.unobserve(entry.target);
      if (entry.target.hidden || session.get(REPORTED_KEY)) return;
      session.set(REPORTED_KEY, '1');
      track('CartCheckoutReturn', {
        context: entry.target.closest('cart-drawer') ? 'drawer' : 'page',
        gap: entry.target.dataset.gap ? Number(entry.target.dataset.gap) : null,
        offered: Number(entry.target.dataset.offered) || 0
      });
    });
  });

  function renderAll() {
    document.querySelectorAll(SELECTOR).forEach(render);
  }

  // The drawer and the cart page bring a new block on every change: filled
  // before the browser paints it (observer callbacks run before the next frame).
  new MutationObserver(() => {
    if (document.querySelector(`${SELECTOR}:not([data-mode])`)) renderAll();
  }).observe(document.body, { childList: true, subtree: true });

  // Cortex first, for the threshold; without an answer the contact line alone.
  // Read once every deferred script ran, so assets/jc-cortex.js exists.
  function start() {
    const ready = window.JapCortex && window.JapCortex.ready ? window.JapCortex.ready : Promise.resolve(null);
    const silent = new Promise((resolve) => setTimeout(() => resolve(null), ANSWER_TIMEOUT_MS));
    Promise.race([ready, silent]).then((state) => {
      cortexState = state;
      cortexKnown = true;
      renderAll();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Back from the checkout with the browser's back button: the page comes out
  // of the back-forward cache as it was before the checkout, so it is decided again.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll(SELECTOR).forEach((block) => block.removeAttribute('data-mode'));
    renderAll();
  });
})();
